import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { Extras } from '@sentry/types';
import { Environment } from 'constants/envConfig';
import { getDsnByEnv } from './sentryEnvConfig';

export default class SentryRepository {
    private sentry: typeof Sentry;

    constructor() {
        if (GLOBAL_CONFIG.environment === Environment.local) {
            return;
        }

        this.sentry = Sentry;

        const sentryConfig = getDsnByEnv(GLOBAL_CONFIG.environment as Environment);

        this.sentry.setContext('env', {
            buildMode: GLOBAL_CONFIG.buildMode,
            environment: GLOBAL_CONFIG.environment,
        });

        this.sentry.init({
            dsn: sentryConfig.dsn,
            integrations: [new Integrations.BrowserTracing()],
            environment: GLOBAL_CONFIG.environment,
            release: GLOBAL_CONFIG.version,
            tracesSampleRate: sentryConfig.tracesSampleRate,
            allowUrls: [
                'stg-ag-iframe.betgames.tv',
                'integrations-ag-iframe.betgames.tv',
                'demo-ag-iframe.betgames.tv',
                'web.twainsports.com',
                'localhost',
            ],
            ignoreErrors: [
                'TypeError: Failed to fetch',
                'TypeError: Load failed',
                'TypeError: NetworkError when attempting to fetch resource.',
                'TypeError: cancelled',
                'TypeError: Cancelled',

                'Error: Not authorized',

                'Error: PARTNER_NOT_AVAILABLE',

                'Error: Load failed',
                'Error: Failed to fetch',

                'ResizeObserver loop limit exceeded',
                /There was an error setting cookie `_pk/,

                // Random plugins/extensions
                'top.GLOBALS',
                // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
                'originalCreateNotification',
                'canvas.contentDocument',
                'MyApp_RemoveAllHighlights',
                'http://tt.epicplay.com',
                'jigsaw is not defined',
                'ComboSearch is not defined',
                'http://loading.retry.widdit.com/',
                'atomicFindClose',
                // Facebook borked
                'fb_xd_fragment',
                // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
                // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
                'bmi_SafeAddOnload',
                'EBCallBackMessageReceived',
                // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
                'conduitPage',
                // Generic error code from errors outside the security sandbox
                // You can delete this if using raven.js > 1.0, which ignores these automatically.
                'Script error.',
                // Avast extension error
                '_avast_submit',
            ],
            denyUrls: [
                // Google Adsense
                /pagead\/js/i,
                // Facebook flakiness
                /graph\.facebook\.com/i,
                // Facebook blocked
                /connect\.facebook\.net\/en_US\/all\.js/i,
                // Woopra flakiness
                /eatdifferent\.com\.woopra-ns\.com/i,
                /static\.woopra\.com\/js\/woopra\.js/i,
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
                // Other plugins
                /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
                /webappstoolbarba\.texthelp\.com\//i,
                /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            ],
        });
    }

    public info = (message: string, extraData: Extras = undefined): void => {
        if (GLOBAL_CONFIG.environment === Environment.local) {
            return;
        }

        this.sentry.captureMessage(message, { extra: extraData });
    };

    public error = (e: Error, context?: Record<string, unknown>): void => {
        if (GLOBAL_CONFIG.environment === Environment.local) {
            return;
        }

        this.sentry.captureException(e, {
            contexts: {
                data: context,
            },
        });
    };

    public setUser = (partner: string): void => {
        if (GLOBAL_CONFIG.environment === Environment.local) {
            return;
        }

        this.sentry.setUser({ username: partner });
    };
}
